import { useConstraintsStore, type LockableField } from '@/state/constraintsStore';
import { useMetadataStore } from '@/state/metadataStore';
import { presetList } from '@/generator/stylePresets';
import type { RoofType, StylePreset } from '@/model/schema';
import { Segmented } from '../controls/Segmented';
import { Stepper } from '../controls/Stepper';
import { LockToggle } from '../controls/LockToggle';

/**
 * Sidebar layout rules:
 *   - Every row has a `label` on the left and the primary control on the
 *     right. A lock toggle (for fields that support soft/hard semantics)
 *     sits in a trailing 22px slot on the far right so a column of locks
 *     aligns vertically down the panel.
 *   - Lock toggles are only shown for numeric ranges, where "soft" actually
 *     has a fallback meaning (generator may push outside). Discrete choices
 *     are always honoured literally, so a lock there would be decorative.
 */

const ROOF_TYPES: Array<{ id: RoofType; label: string }> = [
  { id: 'flat', label: 'Flat' },
  { id: 'gable', label: 'Gable' },
  { id: 'hip', label: 'Hip' },
];

const PRESETS: Array<{ id: StylePreset; label: string }> = presetList().map((p) => ({
  id: p.id,
  label: p.label,
}));

export function ConstraintsPanel() {
  const s = useConstraintsStore();
  const lock = (field: LockableField) => () => s.toggleLock(field);

  return (
    <aside className="sidebar panel" aria-label="Constraints">
      <SectionHeader>Style</SectionHeader>
      <FullRow label="Preset">
        <Segmented
          items={PRESETS}
          value={s.stylePreset.value}
          onChange={(v) => s.setStylePreset(v)}
          ariaLabel="Style preset"
        />
      </FullRow>

      <SectionHeader>Footprint</SectionHeader>
      <FullRow
        label="Lot width (m)"
        aside={<LockToggle locked={s.lotWidth.locked} onToggle={lock('lotWidth')} label="Lot width" />}
      >
        <RangeTwoSlider
          min={6}
          max={30}
          step={0.5}
          lo={s.lotWidth.min}
          hi={s.lotWidth.max}
          onChange={(lo, hi) => s.setLotWidth({ min: lo, max: hi })}
          ariaLabel="Lot width range"
        />
      </FullRow>
      <FullRow
        label="Lot depth (m)"
        aside={<LockToggle locked={s.lotDepth.locked} onToggle={lock('lotDepth')} label="Lot depth" />}
      >
        <RangeTwoSlider
          min={6}
          max={30}
          step={0.5}
          lo={s.lotDepth.min}
          hi={s.lotDepth.max}
          onChange={(lo, hi) => s.setLotDepth({ min: lo, max: hi })}
          ariaLabel="Lot depth range"
        />
      </FullRow>

      <SectionHeader>Stories</SectionHeader>
      <InlineRow label="Story count">
        <Stepper
          value={s.storyCount.value}
          min={1}
          max={3}
          onChange={(v) => s.setStoryCount(v)}
          ariaLabel="Story count"
        />
      </InlineRow>
      <InlineRow label="Basement">
        <input
          type="checkbox"
          checked={s.basement.value}
          onChange={(e) => s.setBasement(e.target.checked)}
          aria-label="Basement"
        />
      </InlineRow>
      <InlineRow label="Garage">
        <input
          type="checkbox"
          checked={s.garage.value}
          onChange={(e) => s.setGarage(e.target.checked)}
          aria-label="Garage"
        />
      </InlineRow>

      <SectionHeader>Rooms</SectionHeader>
      <InlineRow label="Bedrooms">
        <Stepper value={s.bedrooms.value} min={0} max={5} onChange={(v) => s.setBedrooms(v)} ariaLabel="Bedrooms" />
      </InlineRow>
      <InlineRow label="Bathrooms">
        <Stepper value={s.bathrooms.value} min={1} max={4} onChange={(v) => s.setBathrooms(v)} ariaLabel="Bathrooms" />
      </InlineRow>

      <SectionHeader>Roof</SectionHeader>
      <FullRow label="Type">
        <Segmented
          items={ROOF_TYPES}
          value={s.roofType.value}
          onChange={(v) => s.setRoofType(v)}
          ariaLabel="Roof type"
        />
      </FullRow>

      <SectionHeader>Accessories</SectionHeader>
      <InlineRow label="Fence">
        <input
          type="checkbox"
          checked={s.fence.value}
          onChange={(e) => s.setFence(e.target.checked)}
          aria-label="Fence"
        />
      </InlineRow>
      <InlineRow label="Mailbox">
        <input
          type="checkbox"
          checked={s.mailbox.value}
          onChange={(e) => s.setMailbox(e.target.checked)}
          aria-label="Mailbox"
        />
      </InlineRow>
      <InlineRow label="Symmetric front">
        <input
          type="checkbox"
          checked={s.symmetricFront.value}
          onChange={(e) => s.setSymmetricFront(e.target.checked)}
          aria-label="Prefer symmetric front facade"
        />
      </InlineRow>

      <SectionHeader>Site metadata</SectionHeader>
      <MetadataSection />

      <p className="sidebar-note">
        <strong>Locks</strong> only apply to the numeric ranges above. Locked
        ranges are hard constraints the generator must respect; unlocked
        ranges are soft preferences the generator may clamp when the preset
        demands it. Discrete choices (style, story count, roof type, …) are
        always honoured exactly.
      </p>
    </aside>
  );
}

/**
 * Free-form site metadata (address + description). These fields are
 * embedded into every export (USDA, GLB) as custom attributes; they
 * never influence generation. Values are persisted in localStorage so
 * the user doesn't re-type on every reload.
 */
function MetadataSection() {
  const address = useMetadataStore((s) => s.siteAddress);
  const description = useMetadataStore((s) => s.siteDescription);
  const setAddress = useMetadataStore((s) => s.setSiteAddress);
  const setDescription = useMetadataStore((s) => s.setSiteDescription);
  return (
    <>
      <FullRow label="Address">
        <input
          type="text"
          className="text-input"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Elm St, Portland OR"
          aria-label="Site address"
        />
      </FullRow>
      <FullRow label="Description">
        <textarea
          className="text-input text-input--area"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief free-form description…"
          rows={3}
          aria-label="Site description"
        />
      </FullRow>
    </>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <h2>{children}</h2>;
}

/** Two-line row: label + trailing lock on one line, control below. */
function FullRow({
  label,
  aside,
  children,
}: {
  label: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      <div className="field-head">
        <label>{label}</label>
        <span className="field-trailing">{aside}</span>
      </div>
      <div className="field-body">{children}</div>
    </div>
  );
}

/** Single-line row: label on left, control on right. */
function InlineRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="field field--inline">
      <label>{label}</label>
      <div className="field-body field-body--inline">{children}</div>
    </div>
  );
}

interface RangeProps {
  min: number;
  max: number;
  step: number;
  lo: number;
  hi: number;
  onChange: (lo: number, hi: number) => void;
  ariaLabel?: string;
}

function RangeTwoSlider({ min, max, step, lo, hi, onChange, ariaLabel }: RangeProps) {
  return (
    <div className="range-two">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={lo}
        onChange={(e) => onChange(Math.min(Number(e.target.value), hi - step), hi)}
        aria-label={`${ariaLabel ?? ''} min`}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={hi}
        onChange={(e) => onChange(lo, Math.max(Number(e.target.value), lo + step))}
        aria-label={`${ariaLabel ?? ''} max`}
      />
      <div className="range-two-readout">
        {lo.toFixed(1)} – {hi.toFixed(1)} m
      </div>
    </div>
  );
}
